import React from "react";
import { useSiteContent } from "../i18n/SiteContent";
import {
  Buildings, UsersThree, Trophy, Brain,
  Wrench, Lightning, UsersFour,
  MapPinLine, CheckSquare, Globe, ChartLineUp,
} from "@phosphor-icons/react";

const CARD_ICONS = [Buildings, UsersThree, Trophy, Brain, Wrench, Lightning, ChartLineUp, UsersFour];

export default function Highlights() {
  const { pg, pgList } = useSiteContent();
  const heading = pg("highlights.heading", "Trusted across regions and industries");
  const sub = pg("highlights.sub", "Built on decades of experience, a global partner network and deep technological roots — OpSys delivers measurable impact across every sector we touch.");
  const cards = pgList("highlights.cards", []);
  const customersTitle = pg("customers.title", "A strong customer base built in 2024 — UAE & GCC");
  const customers = pgList("customers.list", []);
  const geoTitle = pg("geographic.title", "Geographic development beyond UAE");
  const geoList = pgList("geographic.list", []);

  return (
    <section id="highlights" data-testid="highlights-section" className="py-8 md:py-14 bg-white reveal">
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        {/* Big heading + body (no eyebrow per user request) */}
        <div className="max-w-4xl">
          <h2 className="font-heading font-bold tracking-tight text-4xl sm:text-5xl lg:text-[54px] leading-[1.05] anim anim-up"
            style={{ color: "#0F2A6E", letterSpacing: "-0.025em" }}>
            {heading}
          </h2>
          <p className="mt-6 text-[#1C2B3A] leading-relaxed text-lg md:text-xl font-light max-w-3xl">
            {sub}
          </p>
        </div>

        {/* Capability cards grid (≈ image 3) */}
        {cards.length > 0 && (
          <div className="mt-10 md:mt-12 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 stagger anim" data-testid="highlights-grid">
            {cards.map((c, i) => {
              const Icon = CARD_ICONS[i % CARD_ICONS.length];
              const dark = i % 3 === 0;
              return (
                <div
                  key={i}
                  data-testid={`highlight-card-${i}`}
                  className={`anim anim-up card-lift group relative p-6 rounded-2xl ${dark ? "text-white" : "text-[#0F2A6E]"}`}
                  style={{
                    background: dark
                      ? "linear-gradient(135deg, #0B1F4D 0%, #0F2A6E 100%)"
                      : (i % 3 === 1 ? "#1B6FE0" : "#F5F7FA"),
                    color: dark ? "#FFFFFF" : (i % 3 === 1 ? "#FFFFFF" : "#0F2A6E"),
                  }}
                >
                  <div
                    className="w-10 h-10 rounded-lg flex items-center justify-center"
                    style={{
                      background: dark || i % 3 === 1 ? "rgba(255,255,255,0.15)" : "rgba(27,111,224,0.10)",
                      color: dark || i % 3 === 1 ? "#FFFFFF" : "#1B6FE0",
                    }}
                  >
                    <Icon size={18} weight="duotone" />
                  </div>
                  {c.metric && (
                    <div className={`mt-5 font-heading font-bold text-2xl md:text-3xl tracking-tight ${dark || i % 3 === 1 ? "" : "shimmer-num"}`} style={{ letterSpacing: "-0.02em" }}>
                      {c.metric}
                    </div>
                  )}
                  <div className={`mt-2 font-heading font-semibold text-[15px] tracking-tight leading-snug`} style={{ letterSpacing: "-0.01em" }}>
                    {c.title}
                  </div>
                  {c.desc && <p className={`mt-2 text-[12.5px] leading-relaxed ${dark || i % 3 === 1 ? "text-white/80" : "text-[#6B7A8D]"}`}>{c.desc}</p>}
                </div>
              );
            })}
          </div>
        )}

        {/* Customers + Geographic split */}
        <div className="mt-10 md:mt-12 grid lg:grid-cols-2 gap-6 anim stagger">
          {/* Customers */}
          <div className="anim anim-left relative p-8 md:p-10 rounded-2xl overflow-hidden text-white"
            style={{ background: "linear-gradient(135deg, #0F2A6E 0%, #1B6FE0 100%)" }}>
            <div className="absolute inset-0 grid-bg opacity-10" />
            <div className="relative">
              <div className="inline-flex items-center gap-2 text-[12px] font-semibold uppercase tracking-[0.15em] text-blue-200/90">
                <CheckSquare size={14} weight="duotone" /> Customer base · 2024
              </div>
              <h3 className="mt-3 font-heading font-bold text-2xl md:text-3xl tracking-tight leading-tight" style={{ letterSpacing: "-0.015em" }}>
                {customersTitle}
              </h3>
              <ul className="mt-6 space-y-3" data-testid="customers-list">
                {customers.map((c, i) => (
                  <li key={i} className="flex gap-3 items-center" data-testid={`customer-row-${i}`}>
                    <div className="shrink-0 w-9 h-9 rounded-md flex items-center justify-center font-heading font-bold text-[13px]"
                      style={{ background: "rgba(255,255,255,0.15)" }}>
                      {c.count || (i + 1)}
                    </div>
                    <div>
                      <div className="text-[14.5px] font-semibold leading-snug">{c.label}</div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Geographic expansion */}
          <div className="anim anim-right p-8 md:p-10 rounded-2xl border border-slate-200 bg-white">
            <div className="inline-flex items-center gap-2 text-[12px] font-semibold uppercase tracking-[0.15em]" style={{ color: "#1B6FE0" }}>
              <Globe size={14} weight="duotone" /> Expansion · Beyond UAE
            </div>
            <h3 className="mt-3 font-heading font-bold text-2xl md:text-3xl tracking-tight leading-tight" style={{ color: "#0F2A6E", letterSpacing: "-0.015em" }}>
              {geoTitle}
            </h3>
            <ul className="mt-6 space-y-5" data-testid="geographic-list">
              {geoList.map((g, i) => (
                <li key={i} className="flex gap-3 items-start" data-testid={`geo-row-${i}`}>
                  <div className="mt-1 shrink-0 w-8 h-8 rounded-md flex items-center justify-center"
                    style={{ background: "rgba(27,111,224,0.10)", color: "#1B6FE0" }}>
                    <MapPinLine size={14} weight="duotone" />
                  </div>
                  <div>
                    <div className="text-[14.5px] font-semibold leading-snug" style={{ color: "#0F2A6E" }}>{g.region}</div>
                    {g.detail && <div className="text-[12.5px] text-[#6B7A8D] leading-relaxed mt-1">{g.detail}</div>}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}
